import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useDebounce } from './useDebounce';

export function useSearch(searchParamName: string) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);

  const cleanAllQueryParams = useCallback(() => {
    const params = new URLSearchParams(searchParams);

    router.push(pathname + '?' + params.toString());
  }, [pathname, router, searchParams]);

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams);
      params.set(name, value);
      return params.toString();
    },
    [searchParams]
  );
  const handleParamSearch = useDebounce(
    useCallback(
      (newSearch: string) => {
        const url =
          pathname +
          '?' +
          createQueryString(searchParamName, String(newSearch));
        router.push(url);
      },
      [createQueryString, pathname, router, searchParamName]
    ),
    1500
  );

  const handleSearch = (searchValue) => {
    if (searchValue !== searchParams.get(searchParamName)) {
      setLoading(true);
    } else {
      setLoading(false);
    }
    return handleParamSearch(searchValue);
  };

  const searchParamsValue = useMemo(() => {
    return searchParams.get(searchParamName) || undefined;
  }, [searchParams, searchParamName]);

  useEffect(() => {
    setLoading(false);
  }, [searchParamsValue]);

  return {
    searchLoading: loading,
    handleSearch,
    searchParamsValue,
    cleanAllQueryParams,
  };
}
